import React, { ReactNode } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import ReactTable, { Column, RowInfo, SubComponentFunction } from 'react-table';

import 'react-table/react-table.css';

type DataTableProps = {
    tableData?: any[];
    tableIsLoading?: boolean;
    tableColumns?: any;
    tablePages?: number;
    defaultPageSize?: number;
    filterEnabled?: boolean;
    filterApproved?: boolean;
    SubComponent?: SubComponentFunction | undefined;
    page?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChanged?: (newPageSize: number, newPage: number) => void;

    renderHeader?: () => ReactNode;
    renderTitle?: () => ReactNode;
    onSearch?: (searchText?: string) => void;
    renderFilters?: () => ReactNode;
    onFilterEnabledChange?: (enabled?: boolean) => void;
    onFilterApprovedChange?: (approved?: boolean) => void;
    onCreate?: () => void;
    onRowClick?: (row: any) => void;
    onGroupSelect?: (all: boolean) => void;
    onFetchData?: (state?: any, instance?: any) => void;
} & InjectedIntlProps;

interface DataTableState {
    searchText: string | null;
}

class DataTable extends React.Component<DataTableProps, DataTableState> {

    private dataTable: ReactTable<any> | null;

    constructor(props: any) {
        super(props);
        this.state = {
            searchText: null,
        };
    }

    public getTableState(): Readonly<any> {
        return this.dataTable ? this.dataTable.state : {};
    }

    private onSearchClick() {
        if (this.props.onSearch && this.state.searchText) {
            this.props.onSearch(this.state.searchText);
        }
    }

    private onFetchData(state?: any, instance?: any) {
        if (this.props.onFetchData) {
            this.props.onFetchData(state, instance);
        }
    }

    private onCreateClick() {
        if (this.props.onCreate) {
            this.props.onCreate();
        }
    }

    private onRowClick(finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any) {
        if (rowInfo && this.props.onRowClick) {
            return {
                onClick: () => {
                    // @ts-ignore
                    this.props.onRowClick(rowInfo.original);
                },
            };
        }
        return {};
    }

    private onGroupSelect(all: boolean) {
        if (this.props.onGroupSelect) {
            this.props.onGroupSelect(all);
        }
    }

    private onFilterEnabled(enabled?: boolean) {
        if (this.props.onFilterEnabledChange) {
            this.props.onFilterEnabledChange(enabled || false);
        }
    }

    public render() {
        const { intl, renderHeader } = this.props;
        const createButton = this.props.onCreate && (
            <button type="button" className="btn btn-sm btn-primary" onClick={() => this.onCreateClick()}>
                {intl.formatMessage({ id: 'component.dataTable.create' })}
            </button>
        );

        const searchFields = this.props.onSearch && (
            <div className="input-group">
                <input
                    type="text"
                    placeholder={intl.formatMessage({ id: 'component.dataTable.search.placeholder' })}
                    className="input-sm form-control"
                    onKeyPress={(event) => (event.key === "Enter") ? this.onSearchClick() : null}
                    onChange={(evt) => this.setState({ searchText: evt.target.value })}
                />
                <span className="input-group-btn">
                    <button type="button"
                            onClick={() => this.onSearchClick()}
                            className="btn btn-sm btn-primary">
                        {intl.formatMessage({ id: 'component.dataTable.search.title' })}
                    </button>
                </span>
            </div>
        );

        return (
            <div>
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        {this.props.renderTitle && (
                            <div className="ibox-title">
                                {this.props.renderTitle()}
                            </div>
                        )}
                        <div className="ibox-content">
                            {renderHeader && renderHeader()}
                            <div className="row">
                                {createButton && <div className="col-sm-2">
                                    {createButton}
                                </div>}

                                <div className="col-sm-2"/>

                                <div className="col-sm-2 right">
                                    {this.props.renderFilters && this.props.renderFilters()}
                                </div>

                                {this.props.onSearch && (
                                    <div className="col-sm-2 right">
                                        <div data-toggle="buttons" className={`btn-group ${this.props.onFilterEnabledChange ? "hidden" : ""}`}>
                                            <label
                                                data-tip={'Messages.FilterActiveAndInactive'}
                                                className={`btn btn-sm btn-white ${this.props.filterEnabled ? "active" : ""}`}
                                                onClick={(evt) => this.onFilterEnabled(undefined)}
                                            >
                                                <i className="fa fa-list text-navy"/>
                                            </label>
                                            <label
                                                data-tip={'Messages.FilterActive'}
                                                className={`btn btn-sm btn-white ${this.props.filterEnabled ? "active" : ""}`}
                                                onClick={(evt) => this.onFilterEnabled(true)}
                                                title={'Messages.FilterActive'}
                                            >
                                                <i className="fa fa-check text-navy"/>
                                            </label>
                                            <label
                                                data-tip={'Messages.FilterInactive'}
                                                className={`btn btn-sm btn-white ${this.props.filterEnabled ? "" : "active"}`}
                                                onClick={(evt) => this.onFilterEnabled(false)}
                                                title={'Messages.FilterInactive'}
                                            >
                                                <i className="fa fa-times text-navy"/>
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <div className="col-sm-4 right">
                                    <div data-toggle="buttons" className={`btn-group ${this.props.onGroupSelect ? "" : "hidden"}`}>
                                        <label
                                            data-tip={'Messages.SelectAll'}
                                            className="btn btn-sm btn-white"
                                            onClick={(evt) => this.onGroupSelect(true)}
                                        >
                                            <i className="far fa-check-square text-navy"/>
                                        </label>
                                        <label
                                            data-tip={'Messages.SelectNone'}
                                            className="btn btn-sm btn-white"
                                            onClick={(evt) => this.onGroupSelect(false)}
                                        >
                                            <i className="far fa-square text-navy"/>
                                        </label>

                                    </div>
                                    {searchFields}
                                </div>
                            </div>
                            <div className="table-responsive">
                                <ReactTable
                                    ref={(ref: any) => this.dataTable = ref}
                                    data={this.props.tableData}
                                    loading={this.props.tableIsLoading}
                                    onFetchData={(state, instance) => this.onFetchData(state, instance)}
                                    columns={this.props.tableColumns}
                                    pages={this.props.tablePages}
                                    multiSort={false}
                                    manual={true}
                                    defaultPageSize={this.props.defaultPageSize}
                                    className="-striped -highlight"
                                    getTdProps={(finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any) => this.onRowClick(finalState, rowInfo, column, instance)}
                                    style={{ cursor: 'pointer' }}
                                    collapseOnDataChange={false}
                                    SubComponent={this.props.SubComponent}
                                    page={this.props.page}
                                    pageSize={this.props.pageSize}
                                    onPageChange={this.props.onPageChange}
                                    onPageSizeChange={this.props.onPageSizeChanged}

                                    nextText={intl.formatMessage({ id: 'component.dataTable.next' })}
                                    previousText={intl.formatMessage({ id: 'component.dataTable.previous' })}
                                    loadingText={intl.formatMessage({ id: 'component.dataTable.loading' })}
                                    noDataText={intl.formatMessage({ id: 'component.dataTable.noData' })}
                                    pageText={intl.formatMessage({ id: 'component.dataTable.page' })}
                                    ofText={intl.formatMessage({ id: 'component.dataTable.of' })}
                                    rowsText={intl.formatMessage({ id: 'component.dataTable.rows' })}

                                    minRows={1}

                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div>&nbsp;</div>
            </div>
        );
    }
}

export default injectIntl(DataTable);



// WEBPACK FOOTER //
// ./src/components/DataTable.tsx